import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { CheckCircle, AlertCircle, Clock, Home as HomeIcon, Ban, Shield } from "lucide-react";

export default function TermsAndConditions() {
  const sections = [
    {
      id: "bookings-payments",
      title: "Bookings & Payments",
      icon: CheckCircle,
      items: [
        "All bookings must be made in advance via our website.",
        "Full payment is required at the time of booking, processed securely through Razorpay.",
        "Payment must be made in advance to the caretaker for food, beverages or any other reasonable commodities that are requested by the guests."
      ]
    },
    {
      id: "check-in-out",
      title: "Check-in & Check-out",
      icon: Clock,
      items: [
        "Check-in time: 12:00 PM | Check-out time: 10:00 AM",
        "Early check-in or late check-out is subject to availability and may incur additional charges."
      ]
    },
    {
      id: "house-rules",
      title: "House Rules",
      icon: HomeIcon,
      items: [
        "Smoking is strictly prohibited inside Rooms & in the Swimming Pool Area.",
        "Smoking & Alcohol is not allowed in the Swimming Pool Area.",
        "Guests should access the swimming pool only with proper swimming attire or nylon clothes.",
        "Unmarried couples are not allowed.",
        "Pets are not allowed.",
        "Guests must adhere to noise regulations to ensure a peaceful environment for neighbours.",
        "Guests should not pluck fruits, leaves & flowers.",
        "Guests are requested to use Dustbin for dumping wastes.",
        "Guests are requested not to engage in a conversation with local neighbours.",
        "Guests should not park the vehicle outside of the property.",
        "We do not accommodate EV charging.",
        "Any damages caused to property, furnishings, appliances, gadgets and cutlery sets will be charged to the guest."
      ]
    },
    {
      id: "liability",
      title: "Liability",
      icon: Shield,
      items: [
        "WyldWoods Rural Retreat is not responsible for loss, theft, or damage to personal belongings.",
        "Guests are responsible for their own safety and should follow all provided safety guidelines."
      ]
    },
    {
      id: "modifications-cancellations",
      title: "Modifications & Cancellations",
      icon: Ban,
      items: [
        "Guests may modify their booking up to a week before the stay.",
        "Cancellation policies apply."
      ]
    }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#FDFBF7]">
      <Header />
      <PageBanner
        title="Terms & Conditions"
        description="Please read our terms carefully before making a booking"
        backgroundImage="/assets/banner-bg.jpg"
      />
      
      <main className="flex-grow">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Introduction */}
          <div className="bg-gradient-to-r from-[#E67E22]/10 to-[#F39C12]/10 border-l-4 border-[#E67E22] rounded-lg p-6 mb-12">
            <div className="flex items-start">
              <AlertCircle className="text-[#E67E22] mr-4 flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className="font-serif text-xl font-bold text-[#2C3E50] mb-2">
                  Welcome to WyldWoods Rural Retreat
                </h2>
                <p className="text-[#555] leading-relaxed">
                  By making a booking, you agree to the following terms and conditions. 
                  Please ensure to review the{" "}
                  <Link to="/facilities-and-faqs" className="text-[#E67E22] hover:underline font-semibold">
                    FAQs
                  </Link>{" "}
                  as well for additional information.
                </p>
              </div>
            </div>
          </div>

          {/* Terms Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <div
                key={section.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 p-8 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#E67E22] to-[#F39C12] flex items-center justify-center mr-4">
                    <section.icon className="text-white" size={24} />
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-[#2C3E50]">
                    {index + 1}. {section.title}
                  </h3>
                </div>
                <ul className="space-y-3 ml-16">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start group">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#E67E22] mr-3 mt-2 flex-shrink-0 group-hover:scale-125 transition-transform"></span>
                      <span className="text-[#555] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <div className="mt-12 bg-[#2C3E50] text-white rounded-xl p-8 text-center">
            <p className="text-lg leading-relaxed mb-4">
              By completing a booking, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions.
            </p>
            <p className="text-gray-300 text-sm">
              For any questions or clarifications, please{" "}
              <Link to="/contact" className="text-[#F39C12] hover:underline font-semibold">
                contact us
              </Link>
              .
            </p>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center text-[#E67E22] hover:text-[#F39C12] font-semibold transition-colors"
            >
              <HomeIcon size={20} className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
